import apiEndPoints from '../../../routeConfig';
import { ApiService } from '../../../../service/api.service';
import { attempt, resultMessage } from '../../../../service/apiResult';

/**
 * Climate reference baselines for the WEATHER half of eK Score.
 *
 * Two of the engine's signals compare live NASA POWER observations against a reference value this
 * platform has no other source for, so both tables ship empty (V360__risk_reference_baselines.sql)
 * and the signal reports itself not-applicable until a row exists — it never guesses:
 *
 *   C1_RAINFALL_DEFICIT       risk.rainfall_baselines             per state + season
 *   F5_SOIL_MOISTURE_ADEQUACY risk.crop_soil_moisture_thresholds  per crop
 *
 * The third table V360 created, risk.msp_reference, feeds E1_MSP_GAP — a market/price signal, not
 * a climate one — so it is deliberately not managed here.
 *
 * Backed by /api/v1/ekutir/climate. The duplicate and range rules below are also enforced
 * server-side; they are kept here so the form can mark the offending field rather than only
 * surfacing a message after a round trip.
 */

const apiService = new ApiService();

const body = (result) => result?.data?.data ?? result?.data ?? null;

export const SEASONS = [
  { code: 'KHARIF', label: 'Kharif', window: 'Jun – Oct', startsOn: '1 June' },
  { code: 'RABI', label: 'Rabi', window: 'Nov – Mar', startsOn: '1 November' },
  { code: 'ZAID', label: 'Zaid', window: 'Apr – May', startsOn: '1 April' },
];

export const SEASON_CODES = SEASONS.map((season) => season.code);

export const SOIL_MOISTURE_UNIT = 'GWETROOT_FRACTION';

/**
 * How a configured baseline turns into a risk value, copied from WeatherEnrichmentProvider so the
 * screen can show the consequence of a number rather than just accepting it. Both signals enter
 * the WEATHER category with weight 1.5.
 */
export const RAINFALL_BANDS = [
  { label: 'At or above baseline', detail: 'deficit ≤ 0%', risk: 1 },
  { label: 'Mild shortfall', detail: 'deficit ≤ 15%', risk: 3 },
  { label: 'Marked shortfall', detail: 'deficit ≤ 30%', risk: 6 },
  { label: 'Severe shortfall', detail: 'deficit > 30%', risk: 9 },
];

export const SOIL_MOISTURE_BANDS = [
  { label: 'Met', detail: 'observed ÷ threshold ≥ 1.00', risk: 1 },
  { label: 'Slightly short', detail: 'ratio ≥ 0.80', risk: 4 },
  { label: 'Short', detail: 'ratio ≥ 0.50', risk: 7 },
  { label: 'Severely short', detail: 'ratio < 0.50', risk: 10 },
];

export const SIGNAL_WEIGHT = 1.5;
export const SOIL_MOISTURE_WINDOW_DAYS = 30;




/* ---------------------------------------------------------------- rainfall baselines */

export function validateRainfallBaseline(draft, existing = []) {
  const issues = {};

  if (!String(draft.state || '').trim()) {
    issues.state = 'Enter the state name.';
  }
  if (!SEASON_CODES.includes(draft.seasonCode)) {
    issues.seasonCode = 'Choose a season.';
  }

  const mm = Number(draft.baselineSeasonalRainfallMm);
  if (!(mm > 0)) {
    issues.baselineSeasonalRainfallMm = 'Enter the long-term mean in mm, above 0.';
  } else if (mm > 10000) {
    // Cherrapunji's wettest season is around 9,000mm, so anything past this is a typo, and a
    // baseline that is too high makes every farmer in the state look rainfall-deficient.
    issues.baselineSeasonalRainfallMm = 'That is above any recorded seasonal total — check the value.';
  }

  if (!draft.effectiveFrom) {
    issues.effectiveFrom = 'Choose the date this baseline applies from.';
  }

  const clash = existing.find(
    (row) =>
      row.id !== draft.id &&
      row.state.trim().toLowerCase() === String(draft.state || '').trim().toLowerCase() &&
      row.seasonCode === draft.seasonCode &&
      row.effectiveFrom === draft.effectiveFrom
  );
  if (clash) {
    issues.effectiveFrom = 'A baseline for this state, season and date already exists.';
  }

  return issues;
}

export async function getRainfallBaselines() {
  const result = await attempt(() => apiService.get(apiEndPoints.rainfallBaselines));
  if (!result.success) {
    return { success: false, error: { message: resultMessage(result, 'Could not load rainfall baselines.') } };
  }
  return { success: true, data: body(result) || [] };
}

export async function saveRainfallBaseline(draft) {
  const issues = validateRainfallBaseline(draft);
  if (Object.keys(issues).length) return { success: false, error: { message: 'Fix the highlighted fields.' } };

  const payload = {
    state: String(draft.state).trim(),
    seasonCode: draft.seasonCode,
    baselineSeasonalRainfallMm: Number(draft.baselineSeasonalRainfallMm),
    effectiveFrom: draft.effectiveFrom,
    notes: String(draft.notes || '').trim() || null,
  };

  const result = draft.id
    ? await attempt(() => apiService.update(`${apiEndPoints.rainfallBaselines}/${draft.id}`, payload))
    : await attempt(() => apiService.post(apiEndPoints.rainfallBaselines, payload));

  if (!result.success) {
    return { success: false, error: { message: resultMessage(result, 'Could not save the baseline.') } };
  }
  return { success: true, data: body(result) };
}

export async function deleteRainfallBaseline(id) {
  const result = await attempt(() => apiService.delete(`${apiEndPoints.rainfallBaselines}/${id}`));
  if (!result.success) {
    return { success: false, error: { message: resultMessage(result, 'Could not delete the baseline.') } };
  }
  return { success: true, data: null };
}

/* ---------------------------------------------------------------- soil moisture thresholds */

export function validateSoilMoistureThreshold(draft, existing = []) {
  const issues = {};

  if (!String(draft.cropName || '').trim()) {
    issues.cropName = 'Enter the crop name.';
  }

  const threshold = Number(draft.requiredThreshold);
  if (!(threshold > 0)) {
    issues.requiredThreshold = 'Enter a threshold above 0.';
  } else if (threshold > 1) {
    // GWETROOT is a fraction of saturation, never a percentage — 60 instead of 0.6 would make
    // every observed reading look catastrophically short.
    issues.requiredThreshold = 'GWETROOT is a fraction between 0 and 1 (0.60, not 60).';
  }

  const clash = existing.find(
    (row) =>
      row.id !== draft.id &&
      row.cropName.trim().toLowerCase() === String(draft.cropName || '').trim().toLowerCase()
  );
  if (clash) {
    issues.cropName = 'This crop already has a threshold.';
  }

  return issues;
}

export async function getSoilMoistureThresholds() {
  const result = await attempt(() => apiService.get(apiEndPoints.soilMoistureThresholds));
  if (!result.success) {
    return { success: false, error: { message: resultMessage(result, 'Could not load soil moisture thresholds.') } };
  }
  return { success: true, data: body(result) || [] };
}

export async function saveSoilMoistureThreshold(draft) {
  const issues = validateSoilMoistureThreshold(draft);
  if (Object.keys(issues).length) return { success: false, error: { message: 'Fix the highlighted fields.' } };

  const payload = {
    cropId: draft.cropId || null,
    cropName: String(draft.cropName).trim(),
    requiredThreshold: Number(draft.requiredThreshold),
    notes: String(draft.notes || '').trim() || null,
  };

  const result = draft.id
    ? await attempt(() => apiService.update(`${apiEndPoints.soilMoistureThresholds}/${draft.id}`, payload))
    : await attempt(() => apiService.post(apiEndPoints.soilMoistureThresholds, payload));

  if (!result.success) {
    return { success: false, error: { message: resultMessage(result, 'Could not save the threshold.') } };
  }
  return { success: true, data: body(result) };
}

export async function deleteSoilMoistureThreshold(id) {
  const result = await attempt(() => apiService.delete(`${apiEndPoints.soilMoistureThresholds}/${id}`));
  if (!result.success) {
    return { success: false, error: { message: resultMessage(result, 'Could not delete the threshold.') } };
  }
  return { success: true, data: null };
}

/* ---------------------------------------------------------------- helpers */

export function seasonLabel(code) {
  return SEASONS.find((season) => season.code === code)?.label || code;
}

/** The risk value C1 would produce for an observed seasonal total against a configured baseline. */
export function rainfallRiskFor(baselineMm, observedMm) {
  const deficit = (baselineMm - observedMm) / baselineMm;
  if (deficit <= 0) return 1;
  if (deficit <= 0.15) return 3;
  if (deficit <= 0.3) return 6;
  return 9;
}

/** The observed seasonal totals at which a baseline crosses into each next risk band. */
export function rainfallBandEdges(baselineMm) {
  const mm = Number(baselineMm) || 0;
  return {
    mild: mm * 0.85,
    marked: mm * 0.7,
  };
}
